import React from 'react';
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  FolderIcon,
  MapPinIcon,
  TruckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

const EmployeeInfoModal = ({
  employee,
  onClose,
}: {
  employee: {
    name: string;
    work_email: string;
    mobile_phone: string;
    status: boolean;
    company: { name: string };
    project: { name: string };
    territories: Array<{ id: number; name: string }>;
    warehouses: Array<{ id: number; name: string }>;
    vehicle?: { name: string };
    meter_reading?: string;
    manager?: {
      name: string;
      work_email?: string;
      mobile_phone?: string;
    };
  };
  onClose: () => void;
} & React.HTMLProps<HTMLDivElement>) => {
  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-4 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b-2 border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                <UserIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Employee Details
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Complete employee information
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto px-6 py-6 flex-1">
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-blue-100 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-blue-500"></div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                    Basic Information
                  </h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <UserCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                        Name
                      </p>
                      <p className="text-sm text-gray-900 dark:text-gray-200 font-semibold">
                        {employee?.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <EnvelopeIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                        Email
                      </p>
                      <p className="text-sm text-gray-900 dark:text-gray-200">
                        {employee?.work_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <PhoneIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 font-medium">Phone</p>
                      <p className="text-sm text-gray-900">
                        {employee?.mobile_phone}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-5 w-5 mt-0.5 flex items-center justify-center">
                      <div
                        className={`h-3 w-3 rounded-full ${
                          employee?.status ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      ></div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 font-medium">
                        Status
                      </p>
                      <p
                        className={`text-sm font-semibold ${
                          employee?.status ? 'text-green-700' : 'text-red-700'
                        }`}
                      >
                        {employee?.status ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Organization */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-5 border border-purple-100 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-purple-500"></div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                    Organization
                  </h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <BuildingOfficeIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                        Company
                      </p>
                      <p className="text-sm text-gray-900 dark:text-gray-200 font-semibold">
                        {employee?.company?.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FolderIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                        Project
                      </p>
                      <p className="text-sm text-gray-900 dark:text-gray-200 font-semibold">
                        {employee?.project?.name}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Territories */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-5 border border-green-100 dark:border-green-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-green-500"></div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                    Territories
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {employee?.territories?.length > 0 ? (
                    employee.territories.map((territory: any) => (
                      <span
                        key={territory.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-sm font-medium text-green-700 dark:text-green-300 shadow-sm"
                      >
                        <MapPinIcon className="h-4 w-4" />
                        {territory.name}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No territories assigned
                    </p>
                  )}
                </div>
              </div>

              {/* Warehouses */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-100 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-amber-500"></div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                    Warehouses
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {employee?.warehouses?.length > 0 ? (
                    employee.warehouses.map((warehouse: any) => (
                      <span
                        key={warehouse.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm font-medium text-amber-700 dark:text-amber-300 shadow-sm"
                      >
                        <BuildingOfficeIcon className="h-4 w-4" />
                        {warehouse.name}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      No warehouses assigned
                    </p>
                  )}
                </div>
              </div>

              {/* Vehicle Information */}
              {(employee?.vehicle || employee?.meter_reading) && (
                <div className="bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 rounded-xl p-5 border border-cyan-100 dark:border-cyan-800">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-1 w-1 rounded-full bg-cyan-500"></div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      Vehicle Information
                    </h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {employee?.vehicle && (
                      <div className="flex items-start gap-3">
                        <TruckIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                            Vehicle
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-200 font-semibold">
                            {employee.vehicle.name}
                          </p>
                        </div>
                      </div>
                    )}
                    {employee?.meter_reading && (
                      <div className="flex items-start gap-3">
                        <div className="h-5 w-5 text-cyan-600 dark:text-cyan-400 mt-0.5 flex items-center justify-center">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                            Meter Reading
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-200 font-semibold">
                            {employee.meter_reading}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Manager Information */}
              <div className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-900/20 dark:to-red-900/20 rounded-xl p-5 border border-rose-100 dark:border-rose-800">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-1 w-1 rounded-full bg-rose-500"></div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                    Reporting Manager
                  </h4>
                </div>
                {employee?.manager ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <UserCircleIcon className="h-5 w-5 text-rose-600 dark:text-rose-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                          Name
                        </p>
                        <p className="text-sm text-gray-900 dark:text-gray-200 font-semibold">
                          {employee.manager.name}
                        </p>
                      </div>
                    </div>
                    {employee.manager.work_email &&
                      employee.manager.work_email !== 'false' && (
                        <div className="flex items-start gap-3">
                          <EnvelopeIcon className="h-5 w-5 text-rose-600 dark:text-rose-400 mt-0.5" />
                          <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                              Email
                            </p>
                            <p className="text-sm text-gray-900 dark:text-gray-200">
                              {employee.manager.work_email}
                            </p>
                          </div>
                        </div>
                      )}
                    {employee.manager.mobile_phone && (
                      <div className="flex items-start gap-3">
                        <PhoneIcon className="h-5 w-5 text-rose-600 dark:text-rose-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                            Phone
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-200">
                            {employee.manager.mobile_phone}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No manager assigned
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer - Sticky at bottom */}
          <div className="px-6 py-4 border-t-2 border-gray-100 dark:border-gray-700 flex justify-end flex-shrink-0 bg-white dark:bg-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeInfoModal;
